import { useNavigate } from "@tanstack/react-router";

import { Button } from "./ui.tsx";
import { dismissExpired, useSessionExpired } from "../lib/session.ts";

/**
 * ═══════════════════════════════════════════════════════════════
 *  「登录已过期」顶部提示条
 * ═══════════════════════════════════════════════════════════════
 *
 * 老板：「页面提示登录过期应该在页面顶部有一个弹窗提示，
 * 然后点击停留此页和重新登录，当然停留此页就没内容」。
 *
 * 🔴 **过期了不要把人踢走。** 他正用着，页面上可能有填了一半的东西 ——
 *    直接跳登录页等于把那些东西吃掉，而他还没弄明白发生了什么。
 *    在顶上说一句，让他自己选。
 *
 * 🔴 **「停留此页」之后页面确实是空的，这是说好的。**（老板原话）
 *    数据取不回来，各页会显示各自的空态 —— 但**他知道为什么**，
 *    而不是对着一个莫名其妙变空的页面猜。
 *
 * ⚠️ 只在**登录过一次之后掉线**才出现。游客逛商城时 `/auth/me` 本来就 401，
 *    给他弹「登录过期」是凭空吓人。判断在 `lib/session.ts`。
 *
 * ⚠️ **挂在最外层、`fixed` 定位、z 值压过一切**：过期是全站的事，
 *    不能只在某一页出现；而它必须盖在后台侧栏和弹窗之上，
 *    否则用户看不到它、只看得到一个失灵的页面。
 */
export function SessionExpiredBar() {
  const expired = useSessionExpired();
  const navigate = useNavigate();

  if (!expired) return null;

  return (
    <div className="fixed inset-x-0 top-0 z-[100] flex justify-center px-3 pt-3">
      {/* ⚠️ 限宽居中：横贯 1440 的一条通栏比弹窗更像广告，容易被忽略。 */}
      <div className="flex w-full max-w-[620px] flex-col gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 shadow-lg sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-medium text-amber-900">登录已过期</p>
          <p className="mt-0.5 text-[12px] leading-relaxed text-amber-800">
            为了账号安全，登录状态有有效期。留在这一页的话内容取不回来，重新登录后就恢复正常。
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <Button size="sm" variant="outline" onClick={() => dismissExpired()}>
            停留此页
          </Button>
          <Button
            size="sm"
            onClick={() => {
              dismissExpired();
              /**
               * ⚠️ 带上**当前完整地址**，登完回到原处 ——
               *    只带 pathname 的话，他正在看「提现审核」，登完落在概览页。
               */
              const here = `${window.location.pathname}${window.location.search}`;
              void navigate({ to: "/login", search: { redirect: here } });
            }}
          >
            重新登录
          </Button>
        </div>
      </div>
    </div>
  );
}
